import { ArrowDownward, ArrowUpward, Edit } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    TextField,
    Typography,
    Snackbar,
    Alert,
    Grid,
    Button,
    CircularProgress,
    Box,
} from '@mui/material';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    closestCenter,
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { fetchGracenoteSportsPreferences, fetchTeamPreferences, useGracenoteBulkSportsImportMutation, useGracenoteBulkTeamsImportMutation } from 'core/API/gameMasterSports';
import { TeamSelect } from '../TeamSelect';
import { useTeams } from 'core/hooks/useTeams';
import { useGracenoteTeams } from 'core/hooks/gracenote';
import { GracenoteTeam } from 'types';
import Cookies from 'js-cookie';
import { useQueryClient, useQuery } from 'react-query';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import styles from './GamemasterSportRanking.module.scss';

// Drag Handle Component - only this element should be draggable
const DragHandle = ({ dragHandleListeners }: { dragHandleListeners?: any }) => {
    if (!dragHandleListeners) {
        return (
            <div style={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                <GripVertical style={{ color: '#616161' }} />
            </div>
        );
    }
    
    return (
        <div 
            style={{ marginRight: '8px', cursor: 'grab', display: 'flex', alignItems: 'center', userSelect: 'none', touchAction: 'none' }}
            data-drag-handle
            {...dragHandleListeners}
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }}
        >
            <GripVertical style={{ color: '#616161', pointerEvents: 'none' }} />
        </div>
    );
};

// Sortable Sport Item Component for Edit Mode - single row layout
const SortableSportItemEdit = ({ 
    sport, 
    teamsInfo, 
    maxGames, 
    handleMaxGamesChange, 
    handleTeamSelectChange, 
    selectedTeamsBySport,
    sports
}: any) => {
    // Call useSortable at component level (not in map)
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sport.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    // Get selected teams for this sport to display preview
    const selectedTeams = selectedTeamsBySport[sport.id] || [];
    const teamsPreview = selectedTeams.length > 0 
        ? selectedTeams.map((team: GracenoteTeam) => team.team_brand_name || team.nick_name || team.proper_name).join(', ')
        : '';

    return (
        <div 
            ref={setNodeRef} 
            style={{
                ...style,
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '12px 16px',
                marginBottom: '8px',
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
            {...attributes}
        >
            <DragHandle dragHandleListeners={listeners} />
            
            <Typography style={{ fontWeight: 500, minWidth: '200px', flexShrink: 0 }}>
                {sport?.sport?.label}
            </Typography>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '150px' }}>
                <Typography variant="body2" style={{ color: '#616161', whiteSpace: 'nowrap' }}>
                    Max games:
                </Typography>
                <TextField
                    id={`max-games-${sport.id}`}
                    type="number"
                    value={maxGames[sport.id] ?? 2}
                    onChange={(e) => handleMaxGamesChange(sport.id, e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "-" || e.key === "e") {
                            e.preventDefault();
                        }
                    }}
                    onBlur={(e) => {
                        if (e.target.value === "") {
                            handleMaxGamesChange(sport.id, "2");
                        }
                    }}
                    inputProps={{ min: 1, max: 99 }}
                    style={{ width: '80px' }}
                    variant="outlined"
                    size="small"
                />
            </div>

            <div style={{ flex: 1, minWidth: '300px' }}>
                {teamsInfo?.length > 0 ? (
                    <TeamSelect
                        data={teamsInfo}
                        sport={sport}
                        onChange={(teams) => handleTeamSelectChange(sport.id, teams)}
                        values={sports}
                    />
                ) : (
                    <Typography variant="body2" style={{ color: '#999', fontStyle: 'italic' }}>
                        No teams available
                    </Typography>
                )}
            </div>
        </div>
    );
};

interface GamemasterSportRankingProps {
    onViewModeChange?: (mode: 'list' | 'edit') => void;
    canEdit?: boolean;
}

const GamemasterSportRanking = ({ onViewModeChange, canEdit = false }: GamemasterSportRankingProps) => {
    const { workplaceId } = useWorkplacesContext();
    // State for outer accordion
    const [isOuterExpanded, setIsOuterExpanded] = useState(true);
    const [expandedNested, setExpandedNested] = useState<string | false>(false);
    const [viewMode, setViewMode] = useState<'list' | 'edit'>('list');
    const [sports, setSports] = useState([]);
    const [maxGames, setMaxGames] = useState<Record<string, number | "">>({
        "51": 2,
        "145": 2,
        "8": 2,
        "61": 2,
    });
    const [selectedSport, setSelectedSport] = useState([]);
    const [selectedTeamsBySport, setSelectedTeamsBySport] = useState<Record<string, GracenoteTeam[]>>({});
    const [initialSports, setInitialSports] = useState<any[]>([]);
    const [initialMaxGames, setInitialMaxGames] = useState<Record<string, number | "">>({});
    const [initialSelectedTeamsBySport, setInitialSelectedTeamsBySport] = useState<Record<string, GracenoteTeam[]>>({});
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'success',
    });
    const queryClient = useQueryClient();

    // Move the mutation hook to the top level
    const sportsBulkMutation = useGracenoteBulkSportsImportMutation();

    // Use useQuery to automatically refetch when query is invalidated
    const { data: preferencesData = [], isLoading: isPreferencesLoading, refetch: refetchPreferences } = useQuery(
        ['sportsPreferences', workplaceId],
        () => fetchGracenoteSportsPreferences(workplaceId),
        {
            enabled: !!workplaceId,
            initialData: [],
            retry: false,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            staleTime: 5 * 60 * 1000, // 5 minutes - prevent unnecessary refetches
        }
    );

    // Use useQuery for team preferences to get refetch function
    const { data: teamPreferencesData = [], refetch: refetchTeamPreferences } = useQuery(
        ['teamPreferences'],
        () => fetchTeamPreferences(),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000,
            retry: false,
        }
    );

    useEffect(() => {
        if (!workplaceId) return; // Don't fetch if workplaceId is not available
        
        const fetchData = async () => {
            try {
                // Use preferences from query - no manual fetch needed
                const res = preferencesData || [];
                
                // Transform the sports data to match the expected structure
                const transformedSports = res
                    .filter(sportPref => sportPref && sportPref.sport && sportPref.sport.id) // Filter out invalid entries
                    .map(sportPref => ({
                        id: sportPref.id,
                        sport: {
                            id: sportPref.sport.id,
                            label: sportPref.sport.label,
                            gracenote: sportPref.sport.gracenote || sportPref.sport.id // Preserve gracenote ID for saving, fallback to id
                        },
                        max_number_of_games: sportPref.max_number_of_games || 2,
                        list_order: sportPref.list_order || 1
                    }));
                
                setSports(transformedSports);
                
                // Update maxGames state with values from transformedSports
                const initialMaxGames: Record<string, number> = {};
                transformedSports.forEach(sport => {
                    initialMaxGames[sport.id] = sport.max_number_of_games || 2;
                });
                setMaxGames(prev => ({ ...prev, ...initialMaxGames }));
                
                // Try to fetch team preferences
                let teamPreferences = [];
                try {
                    teamPreferences = await fetchTeamPreferences();
                } catch (teamPrefsError) {
                    console.warn("Team preferences API not available, continuing without saved teams:", teamPrefsError);
                    teamPreferences = [];
                }
                
                // Ensure we have valid sports data before proceeding
                if (transformedSports.length === 0) {
                    console.warn("No valid sports data found, skipping team initialization");
                    setSelectedTeamsBySport({});
                    setSelectedSport([]);
                    return;
                }

                // Initialize selectedTeamsBySport
                // Match teams to sports by sport ID (from team.sport IRI)
                // Key should be preference ID (sport.id), not sport ID
                const initialTeams = {};
                teamPreferences.forEach(pref => {
                    if (!pref.team?.sport?.id) return;
                    
                    // Find matching sport preference by sport ID
                    // team.sport.id is the sport ID (e.g., 63 for Baseball)
                    // We need to find the sport preference that has this sport ID
                    const matchingSport = transformedSports.find(s => 
                        s.sport.id === pref.team.sport.id
                    );
                    
                    if (matchingSport) {
                        // Use preference ID as key (matchingSport.id), not sport ID
                        const sportKey = matchingSport.id.toString();
                        if (!initialTeams[sportKey]) {
                            initialTeams[sportKey] = [];
                        }
                        initialTeams[sportKey].push({
                            id: pref.team.id,
                            team_brand_name: pref.team.team_brand_name,
                            nick_name: pref.team.nick_name,
                            proper_name: pref.team.proper_name,
                            abbreviation: pref.team.abbreviation,
                            team_brand_id: pref.team.team_brand_id,
                            team_id: pref.team.team_id,
                            sport: pref.team.sport,
                            team_organization: pref.team.team_organization,
                        });
                    }
                });

                setSelectedTeamsBySport(initialTeams);
                setSelectedSport(teamPreferences);
                
                // Store initial state for change tracking
                setInitialSports(transformedSports);
                setInitialMaxGames(initialMaxGames);
                setInitialSelectedTeamsBySport(initialTeams);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, [preferencesData, workplaceId]); // React to preferencesData and workplaceId changes

    const toggleOuterAccordion = () => {
        setIsOuterExpanded(!isOuterExpanded);
        if (isOuterExpanded) {
            setExpandedNested(false);
        }
    };

    const handleNestedAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedNested(isExpanded ? panel : false);
    };

    const handleEditModeClick = () => {
        if (!canEdit) {
            return;
        }
        // Store initial state when entering edit mode
        setInitialSports([...sports]);
        setInitialMaxGames({...maxGames});
        setInitialSelectedTeamsBySport({...selectedTeamsBySport});
        setViewMode('edit');
        onViewModeChange?.('edit');
    };

    const handleBackClick = () => {
        // Reset to initial state when going back without saving
        setSports([...initialSports]);
        setMaxGames({...initialMaxGames});
        setSelectedTeamsBySport({...initialSelectedTeamsBySport});
        setViewMode('list');
        onViewModeChange?.('list');
    };

    // Check if changes were made
    const hasChanges = useMemo(() => {
        // Check if sports order changed
        if (sports.length !== initialSports.length) {
            return true;
        }
        const sportsOrderChanged = sports.some((sport, index) => 
            initialSports[index]?.id !== sport.id
        );
        if (sportsOrderChanged) {
            return true;
        }
        
        // Check if maxGames changed
        const maxGamesChanged = Object.keys(maxGames).some(key => 
            maxGames[key] !== initialMaxGames[key]
        ) || Object.keys(initialMaxGames).some(key => 
            maxGames[key] !== initialMaxGames[key]
        );
        if (maxGamesChanged) {
            return true;
        }
        
        // Check if selected teams changed
        const teamsChanged = Object.keys(selectedTeamsBySport).some(sportId => {
            const current = selectedTeamsBySport[sportId] || [];
            const initial = initialSelectedTeamsBySport[sportId] || [];
            if (current.length !== initial.length) {
                return true;
            }
            return current.some((team, index) => team.id !== initial[index]?.id);
        }) || Object.keys(initialSelectedTeamsBySport).some(sportId => {
            const current = selectedTeamsBySport[sportId] || [];
            const initial = initialSelectedTeamsBySport[sportId] || [];
            if (current.length !== initial.length) {
                return true;
            }
            return current.some((team, index) => team.id !== initial[index]?.id);
        });
        
        return teamsChanged;
    }, [sports, initialSports, maxGames, initialMaxGames, selectedTeamsBySport, initialSelectedTeamsBySport]);

    // Notify parent of initial viewMode
    useEffect(() => {
        onViewModeChange?.('list');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMaxGamesChange = (sportId: string, value: string) => {
        let numValue: number | "" = value === "" ? "" : Number(value);

        if (numValue !== "") {
            if (numValue < 1) numValue = 1;
            if (numValue > 99) numValue = 99;
        }

        setMaxGames((prev) => ({
            ...prev,
            [sportId]: numValue,
        }));
    };

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        })
    );

    const teamsPerSport = useTeams(sports);
    const { isLoading: isTeamsLoading } = useGracenoteTeams();

    const handleDragEnd = useCallback(
        (event: any) => {
            const { active, over } = event;

            if (over && active.id !== over.id) {
                const oldIndex = sports.findIndex((sport) => sport.id === active.id);
                const newIndex = sports.findIndex((sport) => sport.id === over.id);

                const newOrder = arrayMove(sports, oldIndex, newIndex);
                setSports(newOrder);
            }
        },
        [sports]
    );

    const handleTeamSelectChange = (sportId: string, teams: GracenoteTeam[]) => {
        setSelectedTeamsBySport(prev => ({
            ...prev,
            [sportId]: teams
        }));
    };

    // Initialize the mutation hook at the top level of your component
    const gracenoteBulkTeamsImportMutation = useGracenoteBulkTeamsImportMutation();

    const saveTeamHandler = async () => {
        if (!workplaceId) {
            setSnackbar({
                open: true,
                message: 'Workplace ID is missing. Please refresh the page.',
                severity: 'error',
            });
            return;
        }

        // Validate that sports array is not empty
        if (!sports || sports.length === 0) {
            setSnackbar({
                open: true,
                message: 'No sports data available. Please wait for data to load.',
                severity: 'error',
            });
            return;
        }

        // Transform sports array into the desired payload format
        // POST API expects sport.id to be gracenote ID, not database ID
        // Payload structure: { workplace: { id }, sports: [{ sport: { id }, maxNumberOfGames, listOrder }] }
        const sportsPayload = sports
            .filter(sport => {
                // Validate sport structure
                if (!sport || !sport.sport) {
                    console.warn('Invalid sport structure:', sport);
                    return false;
                }
                // Check if we have either gracenote ID or sport ID
                const hasGracenoteId = sport.sport.gracenote !== undefined && sport.sport.gracenote !== null;
                const hasSportId = sport.sport.id !== undefined && sport.sport.id !== null;
                if (!hasGracenoteId && !hasSportId) {
                    console.warn('Missing both gracenote ID and sport ID for sport:', sport);
                    return false;
                }
                return true;
            })
            .map((sport, index) => {
                // Get gracenote ID from preferences (sport.sport should have gracenote from API response)
                // Priority: gracenote > id (fallback to id if gracenote is not available)
                const gracenoteId = sport.sport?.gracenote ?? sport.sport?.id;
                
                if (!gracenoteId) {
                    console.error(`Missing gracenote ID for sport after validation:`, sport);
                    return null;
                }
                
                // Ensure maxNumberOfGames is a valid number
                // Use sport.id (preference ID) as key to get maxGames value
                const maxGamesValue = maxGames[sport.id];
                const maxGamesNumber = (maxGamesValue === "" || maxGamesValue === undefined || maxGamesValue === null) 
                    ? (sport.max_number_of_games || 2) // Fallback to sport's max_number_of_games if maxGames state doesn't have it
                    : (typeof maxGamesValue === 'number' ? maxGamesValue : Number(maxGamesValue));
                
                const payloadItem = {
                    sport: { id: Number(gracenoteId) }, // API expects gracenote ID as number
                    maxNumberOfGames: maxGamesNumber || 2, // Use updated max_games from state (camelCase)
                    listOrder: index + 1, // Use index for list_order (camelCase)
                };
                
                // Log for debugging
                console.log(`Sport payload item ${index + 1}:`, payloadItem, 'from sport:', sport);
                
                return payloadItem;
            })
            .filter(item => item !== null); // Remove any null entries

        // Validate sportsPayload is not empty
        if (sportsPayload.length === 0) {
            console.error('Sports payload is empty. Sports array:', sports);
            console.error('MaxGames state:', maxGames);
            setSnackbar({
                open: true,
                message: 'Invalid sports data. Cannot save empty payload. Please refresh the page and try again.',
                severity: 'error',
            });
            return;
        }
        
        // Log the final payload for debugging
        console.log('Final sports payload:', sportsPayload);
        console.log('Sports count:', sportsPayload.length);

        // Check if there are any teams selected across all sports
        const allSelectedTeams = Object.values(selectedTeamsBySport).flat();
        const hasTeams = allSelectedTeams.length > 0 && allSelectedTeams.some(team => team && team.id);
        
        // Build teams payload - always build it if teams exist, even if they haven't changed
        const teamsPayload = hasTeams
            ? allSelectedTeams
                .filter(team => team && team.id) // Filter out invalid teams
                .map((team, index) => ({
                    listOrder: index,
                    workplace: `api/workplaces/${workplaceId}`,
                    team: `api/game_master_teams/${team.id}`,
                }))
            : [];

        // Log payloads for debugging
        console.log('=== Teams Save Check ===');
        console.log('All selected teams:', allSelectedTeams);
        console.log('Has teams:', hasTeams);
        console.log('Teams payload:', teamsPayload);
        console.log('Teams payload length:', teamsPayload.length);
        console.log('Sports payload:', sportsPayload);

        try {
            // Always save teams if there are any teams selected, regardless of whether they changed
            // This ensures teams are saved when editing existing teams
            if (hasTeams && teamsPayload.length > 0) {
                console.log('=== Calling game_master_preference_teams API ===');
                console.log('Endpoint: POST /game_master_preference_teams');
                console.log('Teams payload:', JSON.stringify(teamsPayload, null, 2));
                console.log('Teams count:', teamsPayload.length);
                
                await gracenoteBulkTeamsImportMutation.mutateAsync(teamsPayload);
                console.log('Teams API call successful');
            } else {
                console.log('No teams to save, skipping teams API call');
            }

            // Save sports preferences with the transformed payload
            // API: POST /game_master_preference_sports
            // Payload structure: { workplace: { id }, sports: [{ sport: { id }, maxNumberOfGames, listOrder }] }
            const sportsPreferencesPayload = {
                workplace: { id: workplaceId },
                sports: sportsPayload
            };
            
            // Log payload before sending
            console.log('=== Calling game_master_preference_sports API ===');
            console.log('Endpoint: POST /game_master_preference_sports');
            console.log('Payload:', JSON.stringify(sportsPreferencesPayload, null, 2));
            console.log('Sports count:', sportsPayload.length);
            
            // Validate sports payload is not empty before sending
            if (!sportsPreferencesPayload.sports || sportsPreferencesPayload.sports.length === 0) {
                console.error('ERROR: Sports payload is empty. Cannot save.');
                throw new Error('Sports payload is empty. Cannot save.');
            }
            
            // Validate each sport in payload
            sportsPayload.forEach((sport, index) => {
                if (!sport.sport || !sport.sport.id) {
                    console.error(`ERROR: Sport at index ${index} is missing sport.id:`, sport);
                }
                if (!sport.maxNumberOfGames || sport.maxNumberOfGames < 1) {
                    console.warn(`WARNING: Sport at index ${index} has invalid maxNumberOfGames:`, sport);
                }
                if (!sport.listOrder || sport.listOrder < 1) {
                    console.warn(`WARNING: Sport at index ${index} has invalid listOrder:`, sport);
                }
            });
            
            // Call the API
            console.log('Calling sportsBulkMutation.mutateAsync...');
            const daa = await sportsBulkMutation.mutateAsync(sportsPreferencesPayload);
            console.log('API call successful. Response:', daa);
            
            setSnackbar({
                open: true,
                message: 'Sport ranking saved successfully',
                severity: 'success',
            });

            // Update initial state to match current state after save
            setInitialSports([...sports]);
            setInitialMaxGames({...maxGames});
            setInitialSelectedTeamsBySport({...selectedTeamsBySport});
            
            // Switch back to list view after successful save
            setViewMode('list');
            onViewModeChange?.('list');

            // Extract team IDs from selectedTeamsBySport for setSelectedSport
            const allSelectedTeams = Object.values(selectedTeamsBySport).flat();
            setSelectedSport(
                allSelectedTeams.map((team, index) => ({
                    team: {
                        id: team.id,
                        sport: sports.find((s) =>
                            selectedTeamsBySport[s.id]?.some((t) => t.id === team.id)
                        )?.sport,
                    },
                    list_order: index,
                }))
            );

            // Invalidate and refetch queries to get fresh data
            await queryClient.invalidateQueries(['sportsPreferences', workplaceId]);
            await queryClient.invalidateQueries(['teamPreferences']);
            
            // Refetch the data using the refetch functions from useQuery hooks
            const [refetchedSportsResult, refetchedTeamsResult] = await Promise.all([
                refetchPreferences(),
                refetchTeamPreferences()
            ]);
            
            // Get the fresh data from refetch results
            const freshSportsData = refetchedSportsResult?.data || preferencesData || [];
            const freshTeamPreferences = refetchedTeamsResult?.data || teamPreferencesData || [];
            
            // Update state with fresh data
            let transformedSports: any[] = [];
            if (freshSportsData.length > 0) {
                transformedSports = freshSportsData
                    .filter((sportPref: any) => sportPref && sportPref.sport && sportPref.sport.id)
                    .map((sportPref: any) => ({
                        id: sportPref.id,
                        sport: {
                            id: sportPref.sport.id,
                            label: sportPref.sport.label,
                            gracenote: sportPref.sport.gracenote || sportPref.sport.id
                        },
                        max_number_of_games: sportPref.max_number_of_games || 2,
                        list_order: sportPref.list_order || 1
                    }));
                
                setSports(transformedSports);
                
                // Update maxGames
                const updatedMaxGames: Record<string, number> = {};
                transformedSports.forEach((sport: any) => {
                    updatedMaxGames[sport.id] = sport.max_number_of_games || 2;
                });
                setMaxGames(updatedMaxGames);
                setInitialMaxGames(updatedMaxGames);
                setInitialSports(transformedSports);
            } else {
                // Use current sports if no fresh data
                transformedSports = sports;
            }
            
            // Update teams with fresh data - use transformedSports instead of sports
            if (freshTeamPreferences.length > 0 && transformedSports.length > 0) {
                const updatedTeams: Record<string, GracenoteTeam[]> = {};
                freshTeamPreferences.forEach((pref: any) => {
                    if (!pref.team?.sport?.id) return;
                    
                    const matchingSport = transformedSports.find((s: any) => 
                        s.sport.id === pref.team.sport.id
                    );
                    
                    if (matchingSport) {
                        const sportKey = matchingSport.id.toString();
                        if (!updatedTeams[sportKey]) {
                            updatedTeams[sportKey] = [];
                        }
                        updatedTeams[sportKey].push({
                            id: pref.team.id,
                            team_brand_name: pref.team.team_brand_name,
                            nick_name: pref.team.nick_name,
                            proper_name: pref.team.proper_name,
                            abbreviation: pref.team.abbreviation,
                            team_brand_id: pref.team.team_brand_id,
                            team_id: pref.team.team_id,
                            sport: pref.team.sport,
                            team_organization: pref.team.team_organization,
                        });
                    }
                });
                
                setSelectedTeamsBySport(updatedTeams);
                setInitialSelectedTeamsBySport(updatedTeams);
                
                // Update selectedSport
                setSelectedSport(
                    freshTeamPreferences.map((pref: any, index: number) => ({
                        team: {
                            id: pref.team.id,
                            sport: pref.team.sport,
                        },
                        list_order: index,
                    }))
                );
            } else if (freshTeamPreferences.length === 0) {
                // If no teams, clear the state
                setSelectedTeamsBySport({});
                setInitialSelectedTeamsBySport({});
                setSelectedSport([]);
            }

        } catch (error: any) {
            console.error("=== ERROR saving sport ranking ===");
            console.error("Error details:", error);
            console.error("Error response:", error?.response);
            console.error("Error message:", error?.message);
            
            // Extract error message from response
            let errorMessage = 'Failed to save sport ranking. Please try again.';
            if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
            });
        }
    };

    // Combine loading states - show loader if preferences or teams are loading
    const isLoading = isPreferencesLoading || isTeamsLoading || teamsPerSport === undefined;

    // Helper function to get teams preview for table view
    const getTeamsPreview = (sportId: string) => {
        const selectedTeams = selectedTeamsBySport[sportId] || [];
        if (selectedTeams.length === 0) return '-';
        return selectedTeams
            .map((team: GracenoteTeam) => team.team_brand_name || team.nick_name || team.proper_name)
            .join(', ');
    };

    return (
        <div className={styles.container}>
            {/* Outer Accordion */}
            <div className={styles.accordionHeader}>
                <button onClick={toggleOuterAccordion} style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                    <h5>Sports Ranking</h5>
                </button>
                {viewMode === 'list' && (
                    <button
                        onClick={handleEditModeClick}
                        className={styles.editButton}
                        title={canEdit ? "Edit Sport Ranking" : "You do not have permission to edit"}
                        style={{ marginLeft: 'auto', opacity: canEdit ? 1 : 0.5, cursor: canEdit ? 'pointer' : 'not-allowed' }}
                        disabled={!canEdit}
                    >
                        <Edit className={styles.editIcon} />
                    </button>
                )}
            </div>

            {/* Outer Accordion Content */}
            <div
                className={`${styles.accordionContent} ${isOuterExpanded ? styles.expanded : ''}`}
                style={{ height: isOuterExpanded ? 'max-content' : '0' }}
            >
                    {viewMode === 'list' && (
                        <>
                            <div className={styles.deviceCard}>
                                {/* Loading State - show loader during initial loading (preferences or teams) */}
                                {isLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                                        <CircularProgress size={25} />
                                    </Box>
                                ) : sports.length > 0 ? (
                                    <div className={styles.tableContainer}>
                                        <table className={styles.sportRankingTable}>
                                            <thead>
                                                <tr>
                                                    <th>Sport</th>
                                                    <th>Teams</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sports.map((sport) => (
                                                    <tr key={sport.id}>
                                                        <td>
                                                            {sport?.sport?.label}
                                                        </td>
                                                        <td style={{ color: '#666' }}>
                                                            {getTeamsPreview(sport.id)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className={styles.noDataMessage}>
                                        No record found
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {viewMode === 'edit' && (
                        <>
                            <div className={styles.editSportRankingSetting}>
                                <div className={styles.formContainer}>
                                    <Grid container direction="column" spacing={2} pl={2}>
                                        <Grid container direction="row" justifyContent="space-between">
                                            <Grid item>
                                                <Button className={styles.backButton} onClick={handleBackClick}>
                                                    Back
                                                </Button>
                                            </Grid>
                                            <Grid item xs={6} display={'flex'} justifyContent="flex-end">
                                                <Button
                                                    className={styles.saveButton}
                                                    onClick={saveTeamHandler}
                                                    disabled={!canEdit || gracenoteBulkTeamsImportMutation.isLoading || !hasChanges}
                                                    title={canEdit ? "Save" : "You do not have permission to edit"}
                                                >
                                                    {gracenoteBulkTeamsImportMutation.isLoading ? "Saving..." : 'Save'}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} container direction="row" justifyContent="flex-start" alignItems="center">
                                            <h2 className={styles.formTitle}>Edit Ranking</h2>
                                        </Grid>
                                    </Grid>

                                    {/* Edit Mode - Single Row Layout */}
                                    {isLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                                            <CircularProgress size={25} />
                                        </Box>
                                    ) : (
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <SortableContext
                                                items={sports.map((sport) => sport.id)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                <div className={styles.sportsRankingContainer}>
                                                    {sports.map((sport) => {
                                                        const teamsInfo = teamsPerSport?.[sport.sport.id] || [];
                                                        return (
                                                            <SortableSportItemEdit
                                                                key={sport.id}
                                                                sport={sport}
                                                                teamsInfo={teamsInfo}
                                                                maxGames={maxGames}
                                                                handleMaxGamesChange={handleMaxGamesChange}
                                                                handleTeamSelectChange={handleTeamSelectChange}
                                                                selectedTeamsBySport={selectedTeamsBySport}
                                                                sports={sports}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            </SortableContext>
                                        </DndContext>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
            </div>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default GamemasterSportRanking;